/**
 * Phase F3 — Provider Assignment Inbox
 * -------------------------------------
 * Lets providers view, accept, and decline incoming portfolio requests.
 *
 * Endpoints used:
 *   GET  /api/provider/requests                       — list inbox (PENDING/ACCEPTED/DECLINED/CANCELLED/EXPIRED)
 *   POST /api/provider/requests/{request_id}/accept   — accept request (auto-creates assignment)
 *   POST /api/provider/requests/{request_id}/decline  — decline request
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Inbox, Check, X, Clock, CheckCircle, XCircle, Ban, Briefcase, DollarSign,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'PENDING', label: 'Pending', Icon: Clock, color: theme.colors.status.warning },
  { key: 'ACCEPTED', label: 'Accepted', Icon: CheckCircle, color: theme.colors.status.success },
  { key: 'DECLINED', label: 'Declined', Icon: XCircle, color: theme.colors.status.error },
  { key: 'CANCELLED', label: 'Cancelled', Icon: Ban, color: theme.colors.text.muted },
  { key: 'EXPIRED', label: 'Expired', Icon: Clock, color: theme.colors.text.muted },
];

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const StatusPill = ({ status }) => {
  const meta = TABS.find((t) => t.key === status) || { color: theme.colors.text.muted, label: status };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 999,
      background: `${meta.color}1A`, color: meta.color,
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{meta.label || status}</span>
  );
};

export default function ProviderAssignmentInbox() {
  const [activeTab, setActiveTab] = useState('PENDING');
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [responseDrafts, setResponseDrafts] = useState({}); // id → message

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/provider/requests`, auth);
      setAllRows(res.data?.requests || []);
    } catch (e) {
      toast.error('Failed to load assignment inbox');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => { c[t.key] = 0; });
    allRows.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [allRows]);

  const visible = useMemo(
    () => allRows.filter((r) => r.status === activeTab),
    [allRows, activeTab],
  );

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      const body = { response_message: responseDrafts[id] || null };
      await axios.post(`${API}/provider/requests/${id}/${action}`, body, auth);
      toast.success(`Request ${action === 'accept' ? 'accepted' : 'declined'}`);
      setResponseDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="provider-assignment-inbox-page" style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Inbox size={26} color={theme.colors.primary} />
          Assignment Inbox
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Incoming portfolio engagement requests. Accept to spawn an assignment.
        </p>
      </div>

      {/* Tabs */}
      <div data-testid="inbox-tabs" style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`inbox-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? t.color : theme.colors.borders}`,
                background: active ? `${t.color}15` : theme.colors.bg.card,
                color: active ? t.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <Icon size={16} /> {t.label}
              <span style={{
                marginLeft: 4, background: active ? t.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div data-testid="inbox-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading inbox…
        </div>
      ) : visible.length === 0 ? (
        <div data-testid="inbox-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center' }}>
          <Inbox size={36} color={theme.colors.text.muted} style={{ margin: '0 auto 8px' }} />
          <div style={{ color: theme.colors.text.secondary, fontWeight: 600 }}>
            No {activeTab.toLowerCase()} requests.
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {visible.map((r) => (
            <div
              key={r.id}
              data-testid={`request-row-${r.id}`}
              style={{ ...cardStyle, padding: 18 }}
            >
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                gap: 12, marginBottom: 10, flexWrap: 'wrap',
              }}>
                <div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700,
                    color: theme.colors.text.primary, fontSize: 16, marginBottom: 4,
                  }}>
                    <Briefcase size={14} />
                    {r.portfolio_name || r.portfolio_id}
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.text.secondary }}>
                    From: {r.creator_email || r.creator_id} · Sent {fmtDate(r.created_at)}
                  </div>
                </div>
                <StatusPill status={r.status} />
              </div>

              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13, marginBottom: 12 }}>
                <div>
                  <span style={{ color: theme.colors.text.muted }}>Proposed fee: </span>
                  <span style={{ fontWeight: 700, color: theme.colors.text.primary }}>
                    {r.proposed_fee_type === 'FLAT'
                      ? <><DollarSign size={12} style={{ verticalAlign: 'middle' }} />{r.proposed_fee_flat || 0}</>
                      : <>{r.proposed_fee_percent || 0}%</>}
                  </span>
                </div>
                <div>
                  <span style={{ color: theme.colors.text.muted }}>Decided: </span>
                  <span style={{ fontWeight: 600, color: theme.colors.text.primary }}>{fmtDate(r.decided_at)}</span>
                </div>
              </div>

              {r.message && (
                <div style={{
                  background: theme.colors.bg.secondary, padding: 12,
                  borderRadius: theme.radius.sm, fontSize: 13,
                  color: theme.colors.text.secondary, marginBottom: 12,
                }}>
                  <span style={{ fontWeight: 600, color: theme.colors.text.primary }}>Creator's note: </span>
                  {r.message}
                </div>
              )}

              {r.response_message && (
                <div style={{
                  background: theme.colors.bg.secondary, padding: 12,
                  borderRadius: theme.radius.sm, fontSize: 13,
                  color: theme.colors.text.secondary, marginBottom: 12,
                }}>
                  <span style={{ fontWeight: 600, color: theme.colors.text.primary }}>Your response: </span>
                  {r.response_message}
                </div>
              )}

              {r.status === 'PENDING' && (
                <div>
                  <textarea
                    data-testid={`request-response-${r.id}`}
                    placeholder="Optional response message…"
                    value={responseDrafts[r.id] || ''}
                    onChange={(e) => setResponseDrafts({ ...responseDrafts, [r.id]: e.target.value })}
                    style={{
                      width: '100%', padding: '8px 10px', minHeight: 56,
                      border: `1px solid ${theme.colors.borders}`,
                      borderRadius: theme.radius.sm, fontSize: 13,
                      fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                      marginBottom: 10,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      data-testid={`accept-request-${r.id}`}
                      disabled={busyId === r.id}
                      onClick={() => decide(r.id, 'accept')}
                      style={{
                        padding: '8px 14px', borderRadius: theme.radius.sm, cursor: 'pointer',
                        border: 'none', background: theme.colors.status.success, color: '#fff',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 13, fontWeight: 700,
                      }}
                    >
                      <Check size={14} /> Accept
                    </button>
                    <button
                      data-testid={`decline-request-${r.id}`}
                      disabled={busyId === r.id}
                      onClick={() => decide(r.id, 'decline')}
                      style={{
                        padding: '8px 14px', borderRadius: theme.radius.sm, cursor: 'pointer',
                        border: `1px solid ${theme.colors.borders}`,
                        background: theme.colors.bg.card, color: theme.colors.status.error,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 13, fontWeight: 700,
                      }}
                    >
                      <X size={14} /> Decline
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
